import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import './BodyTypeSelector.css';

export interface BodyTypeInfo {
    id: string;
    title: string;
    description: string;
    imagePath: string;
}

type SliderSize = 'xs' | 's' | 'm' | 'l' | 'xl';
type RegionKey = 'upper' | 'waist' | 'lower';

interface BodyTypeSelectorProps {
    gender: 'female' | 'male';
    selectedBodyType?: string;
    onSelect: (bodyType: string, bodyTypeInfo: BodyTypeInfo) => void;
    apiBaseUrl: string;
    onBack?: () => void;
    initialValues?: { upper: SliderSize; waist: SliderSize; lower: SliderSize };
}

interface SliderConfig {
    label: string;
    sizes: SliderSize[];
    labels: Record<SliderSize, string>;
}

const SIZES: SliderSize[] = ['xs', 's', 'm', 'l', 'xl'];
const SLIDER_INDEX: Record<SliderSize, number> = { xs: 0, s: 1, m: 2, l: 3, xl: 4 };

export const BodyTypeSelector: React.FC<BodyTypeSelectorProps> = ({
    gender,
    selectedBodyType,
    onSelect,
    apiBaseUrl,
    initialValues
}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sliderConfig, setSliderConfig] = useState<Record<RegionKey, SliderConfig> | null>(null);
    const [regionImageBase, setRegionImageBase] = useState('');

    const [upper, setUpper] = useState<SliderSize>(initialValues?.upper || 'm');
    const [waist, setWaist] = useState<SliderSize>(initialValues?.waist || 'm');
    const [lower, setLower] = useState<SliderSize>(initialValues?.lower || 'm');
    const [activeGender, setActiveGender] = useState<'female' | 'male'>(gender);
    const [activeRegion, setActiveRegion] = useState<RegionKey>('upper');
    const [imageLoaded, setImageLoaded] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const loadedImages = useRef<Set<string>>(new Set());
    const displayedImageUrlRef = useRef('');
    const initialNotified = useRef(false);
    const suppressFullPageLoaderRef = useRef(false);

    const markImageUrlLoaded = useCallback((url: string) => {
        if (!url) return;
        loadedImages.current.add(url);
        if (url === displayedImageUrlRef.current) {
            setImageLoaded(true);
        }
    }, []);

    useEffect(() => {
        if (initialValues) {
            setUpper(initialValues.upper);
            setWaist(initialValues.waist);
            setLower(initialValues.lower);
        }
    }, [initialValues]);

    useEffect(() => {
        const fetchConfig = async () => {
            const showFullScreenLoader = !suppressFullPageLoaderRef.current;
            if (showFullScreenLoader) {
                setLoading(true);
            }
            setError(null);
            try {
                const response = await fetch(`${apiBaseUrl}/api/v1/fash-motion/body-type-variants/${activeGender}`);
                if (!response.ok) throw new Error('Vücut tipi verileri yüklenemedi');
                const data = await response.json();
                suppressFullPageLoaderRef.current = true;
                setSliderConfig(data.sliderConfig);
                setRegionImageBase(data.regionImageBase);

                if (selectedBodyType) {
                    const match = selectedBodyType.match(/^(?:female|male)-(xs|s|m|l|xl)-(xs|s|m|l|xl)-(xs|s|m|l|xl)$/);
                    if (match) {
                        setUpper(match[1] as SliderSize);
                        setWaist(match[2] as SliderSize);
                        setLower(match[3] as SliderSize);
                    }
                }
            } catch (err: any) {
                setError(err.message || 'Yüklenirken hata oluştu');
            } finally {
                if (showFullScreenLoader) {
                    setLoading(false);
                }
            }
        };
        fetchConfig();
    }, [activeGender, apiBaseUrl, selectedBodyType]);

    const sizeForRegion = useMemo((): SliderSize => {
        if (activeRegion === 'upper') return upper;
        if (activeRegion === 'waist') return waist;
        return lower;
    }, [activeRegion, upper, waist, lower]);

    const currentImageUrl = useMemo(() => {
        if (!regionImageBase) return '';
        return `${apiBaseUrl}${regionImageBase}/${activeGender}/${activeRegion}/${sizeForRegion}`;
    }, [apiBaseUrl, regionImageBase, activeGender, activeRegion, sizeForRegion]);

    displayedImageUrlRef.current = currentImageUrl;

    useLayoutEffect(() => {
        if (!currentImageUrl) {
            setImageLoaded(false);
            return;
        }
        if (loadedImages.current.has(currentImageUrl)) {
            setImageLoaded(true);
        } else {
            setImageLoaded(false);
        }
    }, [currentImageUrl]);

    const bodyTypeId = useMemo(() => `${activeGender}-${upper}-${waist}-${lower}`, [activeGender, upper, waist, lower]);

    const bodyTypeInfo = useMemo((): BodyTypeInfo => {
        const cfg = sliderConfig;
        const title = cfg
            ? `${cfg.upper.labels[upper]}, ${cfg.waist.labels[waist]}, ${cfg.lower.labels[lower]}`
            : bodyTypeId;
        return {
            id: bodyTypeId,
            title,
            description: title,
            imagePath: regionImageBase ? `${regionImageBase}/${activeGender}/upper/${upper}` : '',
        };
    }, [bodyTypeId, sliderConfig, upper, waist, lower, regionImageBase, activeGender]);

    useEffect(() => {
        if (!loading && sliderConfig && !initialNotified.current) {
            initialNotified.current = true;
            onSelect(bodyTypeId, bodyTypeInfo);
        }
    }, [loading, sliderConfig, bodyTypeId, bodyTypeInfo, onSelect]);

    useEffect(() => {
        if (!regionImageBase) return;
        const regions: RegionKey[] = ['upper', 'waist', 'lower'];
        regions.forEach(region => {
            SIZES.forEach(size => {
                const url = `${apiBaseUrl}${regionImageBase}/${activeGender}/${region}/${size}`;
                if (!loadedImages.current.has(url)) {
                    const img = new Image();
                    img.onload = () => markImageUrlLoaded(url);
                    img.onerror = () => markImageUrlLoaded(url);
                    img.src = url;
                }
            });
        });
    }, [regionImageBase, apiBaseUrl, activeGender, markImageUrlLoaded]);

    const notifyParent = useCallback((u: SliderSize, w: SliderSize, lo: SliderSize, g: 'female' | 'male') => {
        const id = `${g}-${u}-${w}-${lo}`;
        const cfg = sliderConfig;
        const title = cfg
            ? `${cfg.upper.labels[u]}, ${cfg.waist.labels[w]}, ${cfg.lower.labels[lo]}`
            : id;
        onSelect(id, { id, title, description: title, imagePath: regionImageBase ? `${regionImageBase}/${g}/upper/${u}` : '' });
    }, [sliderConfig, onSelect, regionImageBase]);

    const handleSliderChange = useCallback((dimension: RegionKey, value: number) => {
        const size = SIZES[value];
        setActiveRegion(dimension);
        setIsTransitioning(true);
        setTimeout(() => setIsTransitioning(false), 160);

        let u = upper, w = waist, lo = lower;
        if (dimension === 'upper') { setUpper(size); u = size; }
        else if (dimension === 'waist') { setWaist(size); w = size; }
        else { setLower(size); lo = size; }

        notifyParent(u, w, lo, activeGender);
    }, [upper, waist, lower, activeGender, notifyParent]);

    const handleSliderFocus = useCallback((dimension: RegionKey) => {
        if (activeRegion !== dimension) {
            setActiveRegion(dimension);
            setIsTransitioning(true);
            setTimeout(() => setIsTransitioning(false), 160);
        }
    }, [activeRegion]);

    const handleGenderToggle = useCallback((newGender: 'female' | 'male') => {
        if (newGender === activeGender) return;
        setIsTransitioning(true);
        setActiveGender(newGender);
        setUpper('m');
        setWaist('m');
        setLower('m');
        notifyParent('m', 'm', 'm', newGender);
        setTimeout(() => setIsTransitioning(false), 160);
    }, [activeGender, notifyParent]);

    if (loading) {
        return (
            <div className="bts bts--loading">
                <div className="bts__loader">
                    <div className="bts__spinner" />
                    <p>Vücut tipleri yükleniyor...</p>
                </div>
            </div>
        );
    }

    if (error || !sliderConfig) {
        return (
            <div className="bts bts--error">
                <div className="bts__error-box">
                    <span className="bts__error-icon">⚠️</span>
                    <p>{error || 'Veri yüklenemedi'}</p>
                    <button className="bts__retry" onClick={() => window.location.reload()}>Tekrar Dene</button>
                </div>
            </div>
        );
    }

    const renderSlider = (dimension: RegionKey, value: SliderSize) => {
        const config = sliderConfig[dimension];
        if (!config) return null;
        const idx = SLIDER_INDEX[value];
        const isActive = activeRegion === dimension;

        return (
            <div
                className={`bts__slider-row ${isActive ? 'bts__slider-row--active' : ''}`}
                onClick={() => handleSliderFocus(dimension)}
            >
                <div className="bts__slider-header">
                    <span className="bts__slider-label">{config.label}</span>
                    <span className="bts__slider-value">{config.labels[value]}</span>
                </div>
                <div className="bts__slider-track-wrapper">
                    <div className="bts__slider-markers">
                        {SIZES.map((_, i) => (
                            <div
                                key={i}
                                className={`bts__slider-marker ${i === idx ? 'bts__slider-marker--active' : ''} ${i <= idx ? 'bts__slider-marker--filled' : ''}`}
                            />
                        ))}
                    </div>
                    <input
                        type="range"
                        min={0}
                        max={4}
                        step={1}
                        value={idx}
                        onChange={(e) => handleSliderChange(dimension, parseInt(e.target.value))}
                        onFocus={() => handleSliderFocus(dimension)}
                        className="bts__slider-input"
                    />
                </div>
            </div>
        );
    };

    const regionLabel = sliderConfig[activeRegion]?.label || '';

    return (
        <div className="bts">
            <div className="bts__header">
                <h3 className="bts__title">Vücudunuzu Özelleştirin</h3>
                <p className="bts__subtitle">Slider'ları kullanarak vücut tipinizi ayarlayın</p>
            </div>

            <div className="bts__gender-toggle">
                <button
                    className={`bts__gender-btn ${activeGender === 'female' ? 'bts__gender-btn--active' : ''}`}
                    onClick={() => handleGenderToggle('female')}
                >
                    Kadın
                </button>
                <button
                    className={`bts__gender-btn ${activeGender === 'male' ? 'bts__gender-btn--active' : ''}`}
                    onClick={() => handleGenderToggle('male')}
                >
                    Erkek
                </button>
            </div>

            <div className="bts__preview-area">
                <div className="bts__preview-region-label">{regionLabel}</div>
                <div className={`bts__preview-image-wrapper ${isTransitioning ? 'bts__preview--transitioning' : ''}`}>
                    {currentImageUrl && (
                        <img
                            src={currentImageUrl}
                            alt={`${regionLabel} - ${sliderConfig[activeRegion]?.labels[sizeForRegion] || ''}`}
                            className={`bts__preview-image ${imageLoaded ? 'bts__preview-image--loaded' : ''}`}
                            onLoad={() => markImageUrlLoaded(currentImageUrl)}
                            onError={(e) => {
                                markImageUrlLoaded(currentImageUrl);
                                (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23e8e8e8" width="100" height="100" rx="8"/><text x="50" y="55" text-anchor="middle" fill="%23aaa" font-size="12">🧍</text></svg>';
                            }}
                        />
                    )}
                    {!imageLoaded && (
                        <div className="bts__preview-skeleton">
                            <div className="bts__preview-skeleton-pulse" />
                        </div>
                    )}
                </div>
            </div>

            <div className="bts__sliders">
                {renderSlider('upper', upper)}
                {renderSlider('waist', waist)}
                {renderSlider('lower', lower)}
            </div>
        </div>
    );
};

export default BodyTypeSelector;
